import mergeWith from 'lodash.mergewith';
import { type HotelPackageLoaderConfig } from './types';
import HotelPackage from '@itinerary/hotel/HotelPackage';
import ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import HotelPackageLoader, {
    type HotelPackageLoaderResult,
} from '@itinerary/hotel/HotelPackageLoader';
import { type FilterKey } from '@itinerary/ItineraryModuleTypes';
import {
    type HotelFilterData,
    HotelSortBy,
} from '@itinerary/hotel/HotelModuleTypes';

/**
 * Stores data related to a list of packages, that one of them must be chosen
 */
export default class HotelPackageSet extends ItineraryPackageSet<
    HotelFilterData,
    FilterKey,
    HotelPackage
> {
    /**
     * The package loader associated with this set
     */
    protected override loader: HotelPackageLoader;

    get floatingEnabled() {
        return this.loader.floatingEnabled;
    }

    private totalCount = 0;
    private currentPage = 1;
    private _loadingFilters = false;
    private _loadingPrices = false;

    async loadMore() {
        if (this.list.length >= this.totalCount) {
            return;
        }
        const items = await this.loader.loadMore(++this.currentPage);
        this._set = this._set.clone().addAll(items.list);
        await Promise.all([
            this.loadFilterMetadata(),
            this.loadPrices(items.list.map(pkg => pkg.id ?? 0)),
        ]);
    }

    get hasNextPage() {
        return this.list.length < this.totalCount;
    }

    async loadFilterMetadata() {
        this._loadingFilters = true;
        try {
            const filterMetaData = await this.loader.loadFilterMetadata(
                this.list.map(pkg => pkg.id ?? 0)
            );
            this.filterMetaData = mergeWith(
                this.filterMetaData,
                filterMetaData
            );
        } catch (e) {
            reportException(e);
        } finally {
            this._loadingFilters = false;
        }
    }

    async loadPrices(hotelIds?: number[]) {
        if (!hotelIds) {
            // this is called on mount of hotel page static, so let's reset last load time to fix a ssr bug
            this.loader.setLastLoaded();
        }
        this._loadingPrices = true;
        const ids = hotelIds ?? this._list.map(pkg => pkg.id ?? 0);
        try {
            const prices = await this.loader.loadPrices(ids);
            this._list.forEach(pkg => {
                const price = prices[pkg.hotel?.id ?? 0];
                if (price) {
                    pkg.prePayable = price;
                }
            });
        } catch (e) {
            reportException(e);
        } finally {
            this._loadingPrices = false;
        }
    }

    get loadingFilters() {
        return this._loadingFilters;
    }

    get loadingPrices() {
        return this._loadingPrices;
    }

    /**
     * filters
     *
     * @type {HotelSortBy}
     * @memberof HotelPackageSet
     */
    override sortBy: HotelSortBy = HotelSortBy.HigherScore;

    protected override reset(
        config: HotelPackageLoaderConfig,
        filters: Record<FilterKey, any>
    ) {
        super.reset(config, filters);
        this.loader = new HotelPackageLoader(config);
        this.totalCount = 0;
        this.currentPage = 1;
        this.sortBy = HotelSortBy.HigherScore;
    }

    override onGenerate(result: HotelPackageLoaderResult) {
        super.onGenerate(result);
        this.minPrice = result.filterMetaData.minPrice;
        this.totalCount = result.totalCount;
    }

    static revive(from: HotelPackageSet) {
        const clone = Object.assign(
            new HotelPackageSet(
                {
                    adultCount: 1,
                    childCount: 0,
                    roomCount: 1,
                    cityName: '',
                    static: false,
                    toDate: '',
                    fromDate: '',
                },
                {}
            ),
            from
        );
        const prevItems = Array.from(from._set).map(
            pkg => new HotelPackage({ ...pkg })
        );
        clone._set = new GeneralSet<HotelPackage>()
            .setCompareKey('uniqueId')
            .setPriorityOrder('new')
            .addAll(prevItems);
        clone.loader = HotelPackageLoader.revive(from.loader);
        return clone;
    }

    constructor(
        config: HotelPackageLoaderConfig,
        /**
         * Contains filters that must be applied to the results
         */
        public override filters: Record<FilterKey, any>
    ) {
        super(filters);
        this.loader = new HotelPackageLoader(config);
        this._loadingPrices = config.static;
        this._loadingFilters = config.static;
    }
}
